import { css } from "@emotion/react"
import { rgba } from "polished"
import { useBreakpoint } from "gatsby-plugin-breakpoints"

export const globalColors = {
  "white": "#fff",
  "black": "#000",
  "title": "#222",
  "header": "#333",
  "text": "#666",
  "gray": "#777",
  "lightgray": "#f0efeb",
  "gold": "#c0b57c",
  "red": "#e14527",
  "pink": "#f48c94",
  "purple": "#6c5e7e",
}

export function fontSize(size = 16) {
  if (isNaN(size)) {
    size = 16
  }
  return `
    font-size: ${size / 16}rem;
  `
}

export function _clearFix() {
  return `
    display: block;
    &::before,
    &::after {
      content: "";
      display: block;
      clear: both;
    }
  `
}

const useMixins = () => {
  const bp = useBreakpoint()
  const style = {
    default: {
      mainContentStyle: css`
        padding: 60px 16px 160px;
        width: calc(640px - 16px * 2);
        max-width: calc(100vw - 16px * 2);
      `,
      leadWrapperStyle: css`
        margin-bottom: 40px;
        & > * {
          &:last-child {
            margin-bottom: 0;
          }
        }
      `,
      textStyle: css`
        margin-bottom: 16px;
        ${bp.isTablet ? fontSize(16) : fontSize(13)}
        line-height: 1.8;
      `,
      contentStyle: css`
        padding-top: 24px;
        ${_clearFix()}
        & > * {
          &:last-child {
            margin-bottom: 0;
          }
        }
      `,
      sectionStyle: css`
        &:not(:first-of-type) {
          margin-top: 60px;
        }
      `,
      clearFix: css`
        ${_clearFix()}
      `,
      generalTitleStyle: css`
        padding-bottom: 9px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-bottom: 1px solid ${globalColors.gold};
        ${bp.isTablet ? fontSize(20) : fontSize(14)}
        font-weight: 400;
      `,
      simpleTitleStyle: css`
        margin-bottom: ${ bp.isTablet ? '40px' : '16px' };
        padding-bottom: ${ bp.isTablet ? '24px' : '16px' };
        position: relative;
        ${bp.isTablet ? fontSize(28) : fontSize(20)}
        font-weight: 200;
        text-align: center;
        color: ${globalColors.black};
        &::after {
          content: "";
          display: block;
          width: 40px;
          height: 4px;
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          background-color: ${globalColors.red};
        }
      `,
      goDetailButtonStyle: css`
        margin: ${ bp.isTablet ? '0 40px' : '' };
        width: ${ bp.isTablet ? 'calc( 100% - 40px * 2 )' : '100%' };
        height: ${ bp.isTablet ? '60px' : '40px' };
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${ globalColors.gray };
        position: relative;
        transition: opacity 0.4s ease;
        ${ bp.isTablet ? fontSize(16) : fontSize(13) };
        font-family: "Noto Serif JP", serif;
        color: ${ globalColors.white };
        line-height: 1;
        letter-spacing: 0.1em;
        &::after {
          width: 8px;
          height: 8px;
          content: "";
          display: block;
          border-top: 1px solid ${ globalColors.white };
          border-right: 1px solid ${ globalColors.white };
          position: absolute;
          top: 50%;
          left: 8px;
          transform: translateY(-50%) rotate(45deg);
        }
        &:hover {
          opacity: 0.9;
          transition: opacity 0.2s ease;
        }
        &:visited {
          color: ${ globalColors.white }
        }
      `,
      loadingSpinner: css`
        color: ${ globalColors.white };
        font-size: 10px;
        position: relative;
        text-indent: -9999em;
        transform: translate3d(0, -10em, 0);
        &,
        &::before,
        &::after {
          border-radius: 50%;
          width: 2em;
          height: 2em;
          animation-fill-mode: both;
          animation: load 1.8s infinite ease-in-out;
        }
        &::before,
        &::after {
          content: '';
          position: absolute;
          top: 0;
        }
        &::before {
          left: -3.5em;
          animation-delay: -0.16s;
        }
        &::after {
          left: 3.5em;
          animation-delay: 0.16s;
        }
        @keyframes load {
          0%,
          80%,
          100% {
            box-shadow: 0 3.5em 0 0 ${ rgba(globalColors.white, 0.05) };
          }
          40% {
            box-shadow: 0 2.5em 0 0;
          }
        }
      `,
    },
  }

  return style
}

export default useMixins

export const cmsStyle = {
  news: {
    listItem: css`
      border-bottom: 1px dotted ${rgba(globalColors.gray, 0.5)};
    `,
    content: css`
      padding: 32px 0;
    `,
    subHeader: css`
      margin-bottom: 16px;
    `,
    label: css`
      margin-right: 16px;
      padding: 2px 8px;
      display: inline-block;
      ${fontSize(11)}
      @media screen and (min-width: 768px) {
        ${fontSize(13)}
      }
      font-family: "Noto Sans JP";
      font-weight: 200;
      color: ${globalColors.black};
      &.label {
        &--latest {
          background-color: ${globalColors.pink};
        }
        &--events {
          background-color: ${globalColors.gold};
        }
      }
    `,
    publishedAt: css`
      ${fontSize(11)}
      @media screen and (min-width: 768px) {
        ${fontSize(13)}
      }
      font-family: "Noto Sans JP";
      font-weight: 200;
      color: ${globalColors.gray};
    `,
    title: css`
      margin-bottom: 16px;
      ${fontSize(16)}
      @media screen and (min-width: 768px) {
        ${fontSize(20)}
      }
      font-family: "Noto Serif JP", serif;
      font-weight: 400;
      color: ${globalColors.title};
    `,
    markdown: css`
      p {
        &:not(:first-of-type) {
          margin-top: 16px;
        }
        ${fontSize(13)}
        @media screen and (min-width: 768px) {
          ${fontSize(16)}
        }
      }
      table {
        margin-top: 16px;
      }
      // for Preview page
      > div > div {
        margin-top: 16px;
        & > img {
          width: 100%;
        }
      }
    `,
  },
  archives: {
    markdown: css`
      margin: 0 auto;
      max-width: 640px;
      h1, h2, h3, h4, h5, h6 {
        ${fontSize(14)}
        @media screen and (min-width: 768px) {
          ${fontSize(20)}
        }
        text-align: center;
      }
      h6 {
        margin-bottom: 16px;
        ${fontSize(11)}
        @media screen and (min-width: 768px) {
          ${fontSize(13)}
        }
        text-align: center;
        font-family: "Noto Sans JP";
        font-weight: 200;
        line-height: 1;
        > code {
          width: 120px;
          height: 20px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background-color: ${rgba(globalColors.gold, .2)};
        }
      }
      p {
        margin-top: 16px;
        ${fontSize(13)}
        @media screen and (min-width: 768px) {
          ${fontSize(16)}
        }
      }
      table {
        margin-top: 16px;
        width: 100%;
        table-layout: fixed;
        thead {
          display: none;
        }
        tr {
          td {
            padding: 4px 0 4px 8px;
            vertical-align: top;
            ${fontSize(11)}
            @media screen and (min-width: 768px) {
              ${fontSize(13)}
            }
            &:first-of-type {
              padding: 4px 8px 4px 0;
              width: 36px;
              white-space: nowrap;
            }
          }
        }
      }
      hr {
        margin: 16px 0;
        border: none;
        border-top: 1px dotted ${rgba(globalColors.gray, 0.5)};
      }
      // for Preview page
      > div > div {
        margin-top: 16px;
        img {
          margin: 0 auto;
          width: 100%;
          max-width: 400px;
          display: block;
        }
      }
      .gatsby-resp-image-wrapper {
        max-width: 400px !important;
      }
    `
  },
}